import { defaultLocale, supportedLocales } from '@/models/Language';

export const getLocaleFromUrl = () => {
  const urlLocale = window.location.pathname.split('/')[1];
  const isUrlLocaleSupported = Object.keys(supportedLocales).includes(urlLocale);

  return isUrlLocaleSupported ? urlLocale : defaultLocale;
};

export const getLocaleFromBrowser = (options = {}) => {
  const defaultOptions = { countryCodeOnly: false };

  const opt = { ...defaultOptions, ...options };

  const navigatorLocale =
    navigator.languages !== undefined ? navigator.languages[0] : navigator.language;

  if (!navigatorLocale) {
    return defaultLocale;
  }

  const trimmedLocale = (
    opt.countryCodeOnly ? navigatorLocale.trim().split(/[-_]/)[0] : navigatorLocale.trim()
  ).toLowerCase();

  return trimmedLocale;
};

/**
 * Getting localization string
 * If window.location.pathname = '/' then get it from browser
 * else get it from pathname
 * Also check if it is supported. If not return the default one.
 */
export const getStartingLocale = () => {
  const locale = window.location.pathname === '/' ? getLocaleFromBrowser() : getLocaleFromUrl();
  const isLocaleSupported = Object.keys(supportedLocales).includes(locale);

  return isLocaleSupported ? locale : defaultLocale;
};

/**
 * Returns the translations for requested language via CrowdIn CDN
 * @param {string} languageCode
 */
export const downloadTranslations = async (languageCode: string) => {
  const crowdInDistributionBaseUrl = `${import.meta.env.VITE_CROWDIN_PROXY}/${
    import.meta.env.VITE_CROWDIN_DISTRIBUTION_HASH
  }`;

  const manifestRequest = await fetch(`${crowdInDistributionBaseUrl}/manifest.json`);
  const manifestResponse = (await manifestRequest.json()) as {
    files: string[];
    timestamp: number;
  };

  // Extract file name from Crowdin file path.
  // E.g. \/src\/i18n\/locales\/course-page\/%locale_with_underscore%.json --> course-page
  // eslint-disable-next-line no-useless-escape, prettier/prettier
  const fileNameRegex = new RegExp('\\/src\\/i18n\\/locales\\/(.+)\\/[^/]+.json');
  const sourceFileNames = manifestResponse.files.reduce((names, filePath) => {
    const fileName = fileNameRegex.exec(filePath)?.[1];
    if (fileName) names.push(fileName);
    return names;
  }, [] as string[]);

  const sourceFileUrls = sourceFileNames.map(
    (fileName) =>
      `${crowdInDistributionBaseUrl}/content/src/i18n/locales/${fileName}/${languageCode}.json?timestamp=${manifestResponse.timestamp}`,
  );
  const allTranslations = await Promise.all(sourceFileUrls.map((url) => fetch(url)))
    .then((responses) => Promise.all(responses.map((response) => response.json())))
    .then((jsons) => {
      const translations = {};
      jsons.forEach((json) => {
        Object.assign(translations, { ...json });
      });
      return translations;
    })
    .catch((error) => {
      throw error;
    });

  return allTranslations;
};
